<template>
  <div class="wrapper">
    <div class="main">
      <div class="container-fluid">
        <div class="row mt-5 mb-4 pt-5">
          <div class="col-md-10 ml-auto mr-auto text-center mt-5">
            <h1 class="title">Solar path widget playground</h1>
            <br />
            <h4 class="desc">
              Explore sun position over the day: day, twilight and night
              highlights with rise, meridian and set.
            </h4>
            <p class="text-muted mt-3 mb-0">
              Twilight zones are shown as vertical shaded time windows with
              dashed boundaries. Adjust the time slider or sample step to
              explore.
            </p>
            <br />
          </div>
        </div>
        <div class="row mt-5 mb-3">
          <div class="col-md-11 col-lg-10 mx-auto">
            <div class="card bg-secondary p-3">
              <div class="card-body">
                <div class="form-group row mb-3 align-items-center">
                  <label class="col-form-label col-sm-2 text-right">
                    Season
                  </label>
                  <div class="col-sm-4 col-md-3">
                    <select
                      class="form-control form-control-alternative"
                      v-model="season"
                    >
                      <option
                        v-for="s in seasonOptions"
                        :key="s.value"
                        :value="s.value"
                      >
                        {{ s.label }}
                      </option>
                    </select>
                  </div>
                  <div class="col-sm-6 col-md-5 d-flex align-items-center mt-2 mt-sm-0">
                    <div class="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        class="custom-control-input"
                        id="showTwilightLines"
                        v-model="showTwilightLines"
                      />
                      <label class="custom-control-label" for="showTwilightLines">
                        Show twilight lines
                      </label>
                    </div>
                  </div>
                </div>
                <p class="text-muted small mb-3">
                  <strong>Reference date:</strong>
                  {{ referenceDateFormatted }}
                  <span class="text-muted">
                    — use this date to cross-check rise/set times and altitudes.
                  </span>
                </p>
                <div class="solar-chart-wrap">
                  <div class="solar-svg-wrap">
                    <svg
                      ref="solarSvg"
                      :viewBox="`0 0 ${width} ${height}`"
                      class="solar-svg"
                      preserveAspectRatio="xMidYMid meet"
                    >
              <rect :width="width" :height="height" fill="#f8f8f8" />

              <line
                v-for="s in majorTimes"
                :key="'grid-' + s"
                :x1="xScale(s)"
                :x2="xScale(s)"
                y1="0"
                :y2="height"
                stroke="#d8d8d8"
                stroke-width="1"
              />

              <g v-for="a in gridAngles" :key="'angle-' + a">
                <line
                  :x1="chartLeft"
                  :x2="chartLeft + chartWidth"
                  :y1="yScale(a)"
                  :y2="yScale(a)"
                  stroke="#bfbfbf"
                  stroke-dasharray="2 4"
                />
                <text
                  :x="chartLeft - 10"
                  :y="yScale(a) + 5"
                  text-anchor="end"
                  font-size="13"
                  fill="#4b5563"
                  font-weight="600"
                >
                  {{ a }}°
                </text>
              </g>

              <line
                :x1="chartLeft"
                :x2="chartLeft + chartWidth"
                :y1="horizonY"
                :y2="horizonY"
                stroke="#8a8f96"
                stroke-width="2"
              />

              <!-- Filled night areas (below horizon, behind twilight) -->
              <path
                v-for="(seg, idx) in filledNightAreaPaths"
                :key="'night-' + idx"
                :d="seg.d"
                class="solar-night-fill"
              />
              <!-- Twilight bands -->
              <template v-for="(bandEl, idx) in twilightBandElements">
                <rect
                  v-if="bandEl.type === 'rect'"
                  :key="'tw-rect-' + idx"
                  :x="bandEl.x"
                  :y="bandEl.y"
                  :width="bandEl.width"
                  :height="bandEl.height"
                  :fill="bandEl.fill"
                />
                <line
                  v-else-if="bandEl.type === 'line'"
                  :key="'tw-line-' + idx"
                  :x1="bandEl.x1"
                  :x2="bandEl.x2"
                  :y1="bandEl.y1"
                  :y2="bandEl.y2"
                  :stroke="bandEl.stroke"
                  stroke-width="1.5"
                  stroke-dasharray="5 4"
                />
                <text
                  v-else-if="bandEl.type === 'text'"
                  :key="'tw-text-' + idx"
                  :x="bandEl.x"
                  :y="bandEl.y"
                  text-anchor="middle"
                  font-size="14"
                  font-weight="700"
                  :fill="bandEl.fill"
                  :transform="bandEl.transform"
                >
                  {{ bandEl.text }}
                </text>
              </template>

              <!-- Filled day areas -->
              <path
                v-for="(seg, idx) in filledAreaPaths"
                :key="'fill-' + idx"
                :d="seg.d"
                class="solar-day-fill"
              />

              <!-- Twilight threshold lines -->
              <template v-if="showTwilightLines">
                <g v-for="tl in twilightThresholdLines" :key="'tline-' + tl.alt">
                  <line
                  :x1="chartLeft"
                  :x2="chartLeft + chartWidth"
                  :y1="yScale(tl.alt)"
                  :y2="yScale(tl.alt)"
                  :stroke="tl.stroke"
                  stroke-width="1.5"
                  stroke-dasharray="8 4"
                />
                <text
                  :x="chartLeft + chartWidth + 8"
                  :y="yScale(tl.alt) - 4"
                  text-anchor="start"
                  font-size="13"
                  font-weight="700"
                  :fill="tl.stroke"
                >
                  {{ tl.alt }}°
                </text>
                <text
                  :x="chartLeft + chartWidth + 8"
                  :y="yScale(tl.alt) + 12"
                  text-anchor="start"
                  font-size="12"
                  :fill="tl.stroke"
                >
                  {{ tl.label }}
                </text>
                </g>
              </template>

              <path
                :d="curvePath"
                fill="none"
                stroke="#d5dde2"
                stroke-width="3"
              />

              <text :x="34" :y="horizonY - 4" font-size="28" fill="#ec2f72">
                ↑
              </text>
              <text :x="34" :y="horizonY + 30" font-size="28" fill="#ec2f72">
                ↓
              </text>

              <g v-for="item in riseSetItems" :key="item.label">
                <line
                  :x1="xScale(item.seconds)"
                  :x2="xScale(item.seconds)"
                  :y1="horizonY"
                  :y2="horizonY + 18"
                  stroke="#686868"
                  stroke-width="3"
                />
                <text
                  :x="xScale(item.seconds)"
                  :y="horizonY + 55"
                  text-anchor="middle"
                  font-size="18"
                  fill="#4a4a4a"
                  font-weight="600"
                >
                  {{ item.label }}
                </text>
                <text
                  :x="xScale(item.seconds)"
                  :y="horizonY + 83"
                  text-anchor="middle"
                  font-size="22"
                  fill="#3c3c3c"
                  font-weight="700"
                >
                  {{ hhmmFromSeconds(item.seconds) }}
                </text>
                <text
                  :x="xScale(item.seconds)"
                  :y="horizonY + 110"
                  text-anchor="middle"
                  font-size="18"
                  fill="#4a4a4a"
                >
                  {{ formatHeading(item.az) }}
                </text>
              </g>

              <g
                class="sun-marker"
                @pointerdown="startSunDrag"
              >
                <circle
                  :cx="currentX"
                  :cy="currentY"
                  r="15"
                  fill="transparent"
                />
                <circle
                  :cx="currentX"
                  :cy="currentY"
                  r="5"
                  fill="#ff8b2b"
                  stroke="#222"
                  stroke-width="2"
                />
                <circle
                  :cx="currentX"
                  :cy="currentY"
                  r="10"
                  fill="none"
                  stroke="#222"
                  stroke-width="1.5"
                  stroke-dasharray="2 6"
                />
              </g>
                    </svg>
                  </div>
                  <div class="solar-panel">
              <div class="solar-panel-time">
                {{ hhmmFromSeconds(selectedSeconds) }}
              </div>
              <div class="solar-panel-divider"></div>
              <div class="solar-panel-label">Altitude</div>
              <div class="solar-panel-value">{{ Math.round(currentAlt) }}°</div>
              <div class="solar-panel-label">Heading</div>
              <div class="solar-panel-value">
                {{ headingArrow(currentAz) }} {{ formatHeading(currentAz) }}
              </div>
              <div class="solar-panel-label">Position</div>
              <div class="solar-panel-value solar-panel-value-italic">
                {{ classifyPosition(currentAlt) }}
              </div>
            </div>
                </div>

                <p class="text-muted mt-4 mb-2 solar-legend-desc">
                  <strong>Twilight windows:</strong> civil 0° to -6°, nautical
                  -6° to -12°, astronomical -12° to -18°. Day (above horizon),
                  night (below -18°).
                </p>
                <div class="solar-legend-swatches mb-3">
                  <span class="solar-swatch solar-swatch-day">Day</span>
                  <span class="solar-swatch solar-swatch-night">Night</span>
                  <span class="solar-swatch solar-swatch-civil">Civil</span>
                  <span class="solar-swatch solar-swatch-nautical">Nautical</span>
                  <span class="solar-swatch solar-swatch-astronomical">
                    Astronomical
                  </span>
                </div>
                <div class="form-group mb-0">
                  <label class="small font-weight-bold text-default">
                    Time: {{ hhmmFromSeconds(selectedSeconds) }}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="86400"
                    step="60"
                    v-model.number="selectedSeconds"
                    class="form-control-range w-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-5">
          <div class="col-md-11 col-lg-10 mx-auto">
            <div class="row">
              <div
                v-for="item in solarEventCards"
                :key="item.label"
                class="col-6 col-md-3 mb-3"
              >
                <div class="card solar-event-card">
                  <div class="card-body text-center py-3">
                    <div class="solar-event-label">{{ item.label }}</div>
                    <div class="solar-event-time">{{ item.time }}</div>
                    <div class="solar-event-sub">{{ item.sub }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
// Default payload (winter, 12 Jan 2026) – used before fetch or if fetch fails
const defaultPayload = {
  referenceDate: "2026-01-12",
  t: 1768176000,
  m: 86400,
  e: {
    sun: [
      { s: 30169, t: 257 },
      { s: 44348, t: 4098 },
      { s: 58549, t: 258 },
    ],
  },
  o: {
    sun: {
      az: [
        { e: 5400, p: [351.75, 0.007236, 4.71e-8, -1.53e-11] },
        { e: 10800, p: [-11.03, 0.0087397, -2.335e-7, 2.67e-12] },
        { e: 21600, p: [-9.62, 0.0086127, -2.4511e-7, 3.721e-12] },
        { e: 32400, p: [11.91, 0.0055601, -9.9411e-8, 1.382e-12] },
        { e: 43200, p: [88.899, -0.001397, 1.1084e-7, -7.444e-13] },
        { e: 54000, p: [107.24, -0.0032285, 1.6629e-7, -1.2738e-12] },
        { e: 64800, p: [-253.44, 0.0168763, -2.0778e-7, 1.0493e-12] },
        { e: 75600, p: [-798.194, 0.0418101, -5.88571e-7, 2.9898e-12] },
        { e: 81000, p: [-1525.51, 0.0715653, -9.94251e-7, 4.83305e-12] },
        { e: 86400, p: [4784.58, -0.159959, 1.8376e-6, -6.71362e-12] },
      ],
      alt: [
        { e: 5400, p: [147.722, 0.0003644, -1.667e-7, 2.3e-12] },
        { e: 10800, p: [147.684, 0.000431, -1.8892e-7, 4.371e-12] },
        { e: 16200, p: [150.214, -0.0002646, -1.2463e-7, 2.3733e-12] },
        { e: 21600, p: [154.058, -0.00099131, -7.8653e-8, 1.4001e-12] },
        { e: 27000, p: [155.773, -0.0012409, -6.6537e-8, 1.204e-12] },
        { e: 28380, p: [154.776, -0.0011283, -7.0779e-8, 1.2572e-12] },
        { e: 28740, p: [-815.274, 0.06630613, -1.206823e-6, 0] },
        { e: 28920, p: [145145.199, -15.2076294, 0.000531569681, -6.19466165e-9] },
        { e: 29040, p: [-906629.543, 93.9795562, -0.003246754823, 3.73871708e-8] },
        { e: 29400, p: [140136.287, -14.3008457, 0.000486845937, -5.525307e-9] },
        { e: 29700, p: [15906.593, -1.5931084, 5.3544363e-5, -6.0047394e-10] },
        { e: 32400, p: [-383.657, 0.04814916, -1.576819e-6, 1.66228e-11] },
        { e: 37800, p: [111.17, 0.0024241, -1.68024e-7, 2.15065e-12] },
        { e: 43200, p: [180.911, -0.00317534, -1.8045e-8, 8.1052e-13] },
        { e: 48600, p: [274.308, -0.00964492, 1.3142e-7, -3.4114e-13] },
        { e: 54000, p: [390.413, -0.0168197, 2.7928e-7, -1.35733e-12] },
        { e: 56700, p: [842.275, -0.04181243, 7.401335e-7, -4.19038e-12] },
        { e: 58080, p: [3690.778, -0.19256933, 3.399836e-6, -1.983209e-11] },
        { e: 58740, p: [-1577.818, 0.07849862, -1.24889e-6, 6.74223e-12] },
        { e: 59400, p: [-86112.129, 4.38735765, -7.4459036e-5, 4.2137152e-10] },
        { e: 59760, p: [-1722485.406, 86.97474084, -0.001463849548, 8.21274908e-9] },
        { e: 60120, p: [-1098114.5, 54.76768879, -0.000910467044, 5.04550064e-9] },
        { e: 60480, p: [-690849.541, 34.30914616, -0.000567913499, 3.13371725e-9] },
        { e: 60780, p: [378.209, -0.0161358, 2.6613e-7, -1.2701e-12] },
        { e: 62100, p: [373.454, -0.015902, 2.62295e-7, -1.2492e-12] },
        { e: 64800, p: [364.479, -0.01546974, 2.55357e-7, -1.2121e-12] },
        { e: 75600, p: [447.406, -0.01913758, 3.09398e-7, -1.47731e-12] },
        { e: 81000, p: [1304.78, -0.05327978, 7.627666e-7, -3.48477e-12] },
        { e: 86400, p: [1717.982, -0.06902059, 9.625079e-7, -4.32908e-12] },
      ],
    },
  },
};

function cubic(p, dt) {
  return p[0] + p[1] * dt + p[2] * dt * dt + p[3] * dt * dt * dt;
}

function normalizeAngle(deg) {
  let v = deg % 360;
  if (v < 0) v += 360;
  return v;
}

function hhmmFromSeconds(seconds) {
  const s = ((Math.round(seconds) % 86400) + 86400) % 86400;
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function formatHeading(deg) {
  const dirs = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ];
  const angle = normalizeAngle(deg);
  const idx = Math.round(angle / 22.5) % 16;
  return `${Math.round(angle)}° ${dirs[idx]}`;
}

function headingArrow(deg) {
  const angle = normalizeAngle(deg);
  const arrows = [
    { a: 0, s: "↑" },
    { a: 45, s: "↗" },
    { a: 90, s: "→" },
    { a: 135, s: "↘" },
    { a: 180, s: "↓" },
    { a: 225, s: "↙" },
    { a: 270, s: "←" },
    { a: 315, s: "↖" },
  ];
  let best = arrows[0];
  let bestDiff = 999;
  for (const item of arrows) {
    const diff = Math.min(
      Math.abs(angle - item.a),
      360 - Math.abs(angle - item.a)
    );
    if (diff < bestDiff) {
      best = item;
      bestDiff = diff;
    }
  }
  return best.s;
}

function classifyPosition(alt) {
  if (alt > 0) return "Day";
  if (alt > -6) return "Civil twilight";
  if (alt > -12) return "Nautical twilight";
  if (alt > -18) return "Astronomical twilight";
  return "Night";
}

function inferEvents(events) {
  const out = { rise: null, meridian: null, set: null };
  for (const ev of events) {
    if (ev.t === 257) out.rise = ev.s;
    if (ev.t === 4098) out.meridian = ev.s;
    if (ev.t === 258) out.set = ev.s;
  }
  return out;
}

function getSegmentWindow(segments, seconds) {
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    const prevEdge = i === 0 ? 0 : segments[i - 1].e;
    const nextEdge = seg.e;
    if (seconds <= nextEdge) {
      return { seg, prevEdge, nextEdge };
    }
  }
  const last = segments[segments.length - 1];
  const prevEdge = segments.length > 1 ? segments[segments.length - 2].e : 0;
  return { seg: last, prevEdge, nextEdge: 86400 };
}

function evaluatePiecewise(segments, seconds, mode = "absolute-seconds") {
  const { seg, prevEdge, nextEdge } = getSegmentWindow(segments, seconds);
  let dt = seconds;

  if (mode === "prev-edge") dt = seconds - prevEdge;
  if (mode === "segment-end") dt = seconds - nextEdge;
  if (mode === "segment-start") dt = seconds - seg.e;
  if (mode === "midpoint") dt = seconds - (prevEdge + nextEdge) / 2;
  if (mode === "absolute-seconds") dt = seconds;

  return cubic(seg.p, dt);
}

function evaluateAzimuth(seconds, mode, payload) {
  return normalizeAngle(
    evaluatePiecewise(payload.o.sun.az, seconds, mode || "absolute-seconds")
  );
}

function evaluateAltitudeFromPayload(seconds, mode, payload) {
  const zenith = evaluatePiecewise(
    payload.o.sun.alt,
    seconds,
    mode || "absolute-seconds"
  );
  return 90 - zenith;
}

function visualFallbackAltitude(seconds, rise, set) {
  const daylightPeak = 14.5;
  const nightDepth = -58;
  if (seconds >= rise && seconds <= set) {
    const progress = (seconds - rise) / Math.max(1, set - rise);
    return Math.sin(progress * Math.PI) * daylightPeak;
  }
  if (seconds < rise) {
    const prevSet = set - 86400;
    const span = rise - prevSet;
    const progress = (seconds - prevSet) / Math.max(1, span);
    return nightDepth + (0 - nightDepth) * Math.pow(progress, 0.85);
  }
  const nextRise = rise + 86400;
  const span = nextRise - set;
  const progress = (seconds - set) / Math.max(1, span);
  return (nightDepth - 0) * Math.pow(progress, 0.85);
}

function isReasonableAltitude(value) {
  return Number.isFinite(value) && value >= -95 && value <= 95;
}

function makeSeries(stepSeconds, rise, set, curveMode, polyMode, payload) {
  const pts = [];
  for (let s = 0; s <= 86400; s += stepSeconds) {
    const payloadAlt = evaluateAltitudeFromPayload(s, polyMode, payload);
    const alt =
      curveMode === "payload" && isReasonableAltitude(payloadAlt)
        ? payloadAlt
        : visualFallbackAltitude(s, rise, set);
    pts.push({
      s,
      alt,
      az: evaluateAzimuth(s, polyMode, payload),
      payloadAlt,
    });
  }
  return pts;
}

function pathFromPoints(points, xScale, yScale) {
  return points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"} ${xScale(p.s).toFixed(2)} ${yScale(
          p.alt
        ).toFixed(2)}`
    )
    .join(" ");
}

function getTwilightBand(alt) {
  if (alt < 0 && alt >= -6) return "civil";
  if (alt < -6 && alt >= -12) return "nautical";
  if (alt < -12 && alt >= -18) return "astronomical";
  return null;
}

function buildTimeRangesByBand(points) {
  const ranges = { civil: [], nautical: [], astronomical: [] };
  let currentBand = null;
  let start = null;
  for (const point of points) {
    const band = getTwilightBand(point.alt);
    if (band !== currentBand) {
      if (currentBand && start !== null) {
        ranges[currentBand].push({ start, end: point.s });
      }
      currentBand = band;
      start = band ? point.s : null;
    }
  }
  if (currentBand && start !== null) {
    ranges[currentBand].push({
      start,
      end: points[points.length - 1]?.s ?? start,
    });
  }
  return ranges;
}

const SEASONS = ["winter", "spring", "summer", "autumn"];
const PAYLOADS_URL = "/data/solar-payloads.json";

const ALTITUDE_POLY_MODE = "absolute-seconds";

export default {
  name: "SolarPathWidgetPlayground",
  bodyClass: "contact-page",
  data() {
    return {
      payloads: {},
      season: "winter",
      defaultPayload,
      selectedSeconds: 0,
      sunDragging: false,
      showTwilightLines: true,
      stepSeconds: 60,
      width: 1400,
      height: 295,
      chartLeft: 84,
      chartTop: 8,
      chartWidth: 1088,
      chartHeight: 267,
      yMin: -90,
      yMax: 90,
    };
  },
  computed: {
    payload() {
      return this.payloads[this.season] || this.defaultPayload;
    },
    referenceDateFormatted() {
      const raw = this.payload.referenceDate;
      if (!raw || typeof raw !== "string") return "—";
      const parts = raw.split("-").map(Number);
      const [y, m, d] = parts;
      const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ];
      const monthName = months[m - 1];
      if (!monthName || !d) return raw;
      return `${d} ${monthName} ${y}`;
    },
    rise() {
      const events = inferEvents(this.payload.e.sun);
      return events.rise ?? 6 * 3600;
    },
    meridian() {
      const events = inferEvents(this.payload.e.sun);
      return events.meridian ?? 12 * 3600;
    },
    set() {
      const events = inferEvents(this.payload.e.sun);
      return events.set ?? 18 * 3600;
    },
    seasonOptions() {
      return SEASONS.map((s) => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1),
      }));
    },
    xScale() {
      const { chartLeft, chartWidth } = this;
      return (s) => chartLeft + (s / 86400) * chartWidth;
    },
    yScale() {
      const { chartTop, chartHeight, yMin, yMax } = this;
      return (a) => chartTop + ((yMax - a) / (yMax - yMin)) * chartHeight;
    },
    horizonY() {
      return this.yScale(0);
    },
    points() {
      return makeSeries(
        this.stepSeconds,
        this.rise,
        this.set,
        "payload",
        ALTITUDE_POLY_MODE,
        this.payload
      );
    },
    curvePath() {
      return pathFromPoints(this.points, this.xScale, this.yScale);
    },
    filledAreaPaths() {
      const segments = [];
      let current = [];
      for (const point of this.points) {
        if (point.alt >= 0) {
          current.push(point);
        } else if (current.length) {
          segments.push(current);
          current = [];
        }
      }
      if (current.length) segments.push(current);
      const xScale = this.xScale;
      const yScale = this.yScale;
      const horizonY = this.horizonY;
      return segments.map((segment) => {
        const first = segment[0];
        const last = segment[segment.length - 1];
        const top = segment
          .map(
            (p, i) =>
              `${i === 0 ? "M" : "L"} ${xScale(p.s).toFixed(2)} ${yScale(
                p.alt
              ).toFixed(2)}`
          )
          .join(" ");
        return {
          d: `${top} L ${xScale(last.s).toFixed(2)} ${horizonY.toFixed(
            2
          )} L ${xScale(first.s).toFixed(2)} ${horizonY.toFixed(2)} Z`,
        };
      });
    },
    filledNightAreaPaths() {
      // Night = sun below -18° (below astronomical twilight only)
      const segments = [];
      let current = [];
      for (const point of this.points) {
        if (point.alt < -18) {
          current.push(point);
        } else if (current.length) {
          segments.push(current);
          current = [];
        }
      }
      if (current.length) segments.push(current);
      const xScale = this.xScale;
      const yScale = this.yScale;
      const chartBottom = this.chartTop + this.chartHeight;
      return segments.map((segment) => {
        const first = segment[0];
        const last = segment[segment.length - 1];
        const curve = segment
          .map(
            (p, i) =>
              `${i === 0 ? "M" : "L"} ${xScale(p.s).toFixed(2)} ${yScale(
                p.alt
              ).toFixed(2)}`
          )
          .join(" ");
        return {
          d: `${curve} L ${xScale(last.s).toFixed(2)} ${chartBottom.toFixed(
            2
          )} L ${xScale(first.s).toFixed(2)} ${chartBottom.toFixed(2)} Z`,
        };
      });
    },
    twilightBandElements() {
      const ranges = buildTimeRangesByBand(this.points);
      const config = [
        {
          key: "civil",
          fill: "rgba(255, 214, 102, 0.18)",
          stroke: "rgba(217, 119, 6, 0.95)",
          label: "Civil twilight",
        },
        {
          key: "nautical",
          fill: "rgba(255, 154, 86, 0.16)",
          stroke: "rgba(234, 88, 12, 0.95)",
          label: "Nautical twilight",
        },
        {
          key: "astronomical",
          fill: "rgba(154, 118, 255, 0.14)",
          stroke: "rgba(109, 40, 217, 0.95)",
          label: "Astronomical twilight",
        },
      ];
      const out = [];
      config.forEach(({ key, fill, stroke, label }) => {
        (ranges[key] || []).forEach((range) => {
          const x1 = this.xScale(range.start);
          const x2 = this.xScale(range.end);
          const midX = (x1 + x2) / 2;
          const width = Math.max(1, x2 - x1);
          out.push({
            type: "rect",
            x: x1,
            y: this.chartTop,
            width,
            height: this.chartHeight,
            fill,
          });
          out.push({
            type: "line",
            x1,
            x2: x1,
            y1: this.chartTop,
            y2: this.chartTop + this.chartHeight,
            stroke,
          });
          out.push({
            type: "line",
            x1: x2,
            x2,
            y1: this.chartTop,
            y2: this.chartTop + this.chartHeight,
            stroke,
          });
          out.push({
            type: "text",
            x: midX,
            y: this.chartTop + this.chartHeight / 2,
            fill: stroke,
            transform: `rotate(-90 ${midX} ${
              this.chartTop + this.chartHeight / 2
            })`,
            text: label,
          });
        });
      });
      return out;
    },
    twilightThresholdLines() {
      return [
        { alt: -6, label: "Civil", stroke: "rgba(217, 119, 6, 0.9)" },
        { alt: -12, label: "Nautical", stroke: "rgba(234, 88, 12, 0.9)" },
        { alt: -18, label: "Astronomical", stroke: "rgba(109, 40, 217, 0.9)" },
      ];
    },
    majorTimes() {
      return [0, 6, 12, 18, 24].map((h) => h * 3600);
    },
    gridAngles() {
      return [-90, -60, -30, 0, 30, 60, 90];
    },
    riseSetItems() {
      return [
        {
          label: "Rise",
          seconds: this.rise,
          az: evaluateAzimuth(this.rise, "segment-end", this.payload),
        },
        { label: "Meridian", seconds: this.meridian, az: 180 },
        {
          label: "Set",
          seconds: this.set,
          az: evaluateAzimuth(this.set, "segment-end", this.payload),
        },
      ];
    },
    dayLengthFormatted() {
      const secs = Math.max(0, this.set - this.rise);
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      return `${h}h ${m}m`;
    },
    noonAlt() {
      return evaluateAltitudeFromPayload(
        this.meridian,
        ALTITUDE_POLY_MODE,
        this.payload
      );
    },
    solarEventCards() {
      return [
        {
          label: "Sunrise",
          time: hhmmFromSeconds(this.rise),
          sub: formatHeading(
            evaluateAzimuth(this.rise, "segment-end", this.payload)
          ),
        },
        {
          label: "Solar Noon",
          time: hhmmFromSeconds(this.meridian),
          sub: `${Math.round(this.noonAlt)}° altitude`,
        },
        {
          label: "Sunset",
          time: hhmmFromSeconds(this.set),
          sub: formatHeading(
            evaluateAzimuth(this.set, "segment-end", this.payload)
          ),
        },
        {
          label: "Day Length",
          time: this.dayLengthFormatted,
          sub:
            this.season.charAt(0).toUpperCase() + this.season.slice(1),
        },
      ];
    },
    payloadCurrentAlt() {
      return evaluateAltitudeFromPayload(
        this.selectedSeconds,
        ALTITUDE_POLY_MODE,
        this.payload
      );
    },
    currentAlt() {
      return isReasonableAltitude(this.payloadCurrentAlt)
        ? this.payloadCurrentAlt
        : visualFallbackAltitude(this.selectedSeconds, this.rise, this.set);
    },
    currentAz() {
      return evaluateAzimuth(
        this.selectedSeconds,
        "segment-end",
        this.payload
      );
    },
    currentX() {
      return this.xScale(this.selectedSeconds);
    },
    currentY() {
      return this.yScale(this.currentAlt);
    },
  },
  created() {
    fetch(PAYLOADS_URL)
      .then((res) => res.json())
      .then((data) => {
        this.payloads = data;
      })
      .catch(() => {
        // Keep using defaultPayload
      });
  },
  methods: {
    hhmmFromSeconds,
    formatHeading,
    headingArrow,
    classifyPosition,
    startSunDrag(e) {
      this.sunDragging = true;
      this._sunCaptureTarget = e.target;
      e.target.setPointerCapture(e.pointerId);
      document.addEventListener("pointermove", this.updateSunDrag);
      document.addEventListener("pointerup", this.endSunDrag);
      document.addEventListener("pointercancel", this.endSunDrag);
    },
    updateSunDrag(e) {
      const svg = this.$refs.solarSvg;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const scaleX = this.width / rect.width;
      const svgX = (e.clientX - rect.left) * scaleX;
      const sec =
        ((svgX - this.chartLeft) / this.chartWidth) * 86400;
      this.selectedSeconds = Math.round(
        Math.max(0, Math.min(86400, sec))
      );
    },
    endSunDrag(e) {
      this.sunDragging = false;
      if (this._sunCaptureTarget && e.pointerId !== undefined) {
        try {
          this._sunCaptureTarget.releasePointerCapture(e.pointerId);
        } catch (_) {
          /* releasePointerCapture can throw if capture was already released */
        }
        this._sunCaptureTarget = null;
      }
      document.removeEventListener("pointermove", this.updateSunDrag);
      document.removeEventListener("pointerup", this.endSunDrag);
      document.removeEventListener("pointercancel", this.endSunDrag);
    },
  },
};
</script>

<style scoped>
/* Chart container – theme gray-100 / secondary */
.solar-chart-wrap {
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
  background: #f6f9fc;
  overflow-x: auto;
  display: flex;
  align-items: stretch;
  height: 480px;
}
.solar-svg-wrap {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  overflow: auto;
}
.solar-svg {
  display: block;
  height: 100%;
  width: auto;
  min-width: 1000px;
}
.sun-marker {
  cursor: grab;
}
.sun-marker:active {
  cursor: grabbing;
}
/* Side panel – theme secondary / gray-200 */
.solar-panel {
  margin-left: 1rem;
  width: 200px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
  background: #f4f5f7;
  padding: 1rem 1.25rem;
  text-align: center;
}
.solar-panel-time {
  font-size: 1.5rem;
  font-weight: 600;
  color: #32325d;
  margin-bottom: 0.5rem;
}
.solar-panel-divider {
  height: 0;
  border-top: 1px solid #dee2e6;
  margin: 0.75rem 0;
}
.solar-panel-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #32325d;
  margin-top: 0.25rem;
}
.solar-panel-value {
  font-size: 1rem;
  color: #525f7f;
  margin-bottom: 0.5rem;
}
.solar-panel-value-italic {
  font-style: italic;
}
/* Legend text and swatches below SVG */
.solar-legend-desc {
  font-size: 1.05rem;
}
.solar-legend-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}
.solar-swatch {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.05rem;
  color: #525f7f;
}
.solar-swatch::before {
  content: "";
  display: inline-block;
  width: 1.5rem;
  height: 0.9rem;
  border-radius: 0.25rem;
}
.solar-swatch-day::before {
  background: rgba(186, 210, 222, 0.75);
}
.solar-swatch-night::before {
  background: rgba(30, 41, 59, 0.5);
}
.solar-swatch-civil::before {
  background: rgba(255, 214, 102, 0.5);
}
.solar-swatch-nautical::before {
  background: rgba(255, 154, 86, 0.45);
}
.solar-swatch-astronomical::before {
  background: rgba(154, 118, 255, 0.4);
}
/* SVG fills (day/night on chart) */
.solar-night-fill {
  fill: rgba(30, 41, 59, 0.35);
}
.solar-day-fill {
  fill: rgba(186, 210, 222, 0.6);
}
/* Range input full width */
.form-control-range {
  width: 100%;
  max-width: 100%;
}
/* Solar event summary cards */
.solar-event-card {
  border: 1px solid #e9ecef;
  background: #f6f9fc;
  border-radius: 0.5rem;
  height: 100%;
}
.solar-event-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8898aa;
  margin-bottom: 0.35rem;
}
.solar-event-time {
  font-size: 1.5rem;
  font-weight: 700;
  color: #32325d;
  margin-bottom: 0.2rem;
}
.solar-event-sub {
  font-size: 0.8rem;
  color: #525f7f;
}
</style>
